import { useRef, useState } from 'react';
import { Icons } from '@/components/icons';

interface Props {
  onScan: (file: File) => Promise<void>;
}

const PARSE_STEPS = ['Uploading photo', 'Detecting receipt edges', 'Reading line items', 'Assigning categories'];

export function ReceiptScanner({ onScan }: Props) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB');
      return;
    }

    setError('');
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImageData(String(e.target?.result || ''));
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleParse = async () => {
    if (!imageData || !selectedFile) return;
    setParsing(true);
    setError('');
    try {
      await onScan(selectedFile);
    } catch (nextError) {
      console.error('Parse error:', nextError);
      setError('Could not parse receipt. Try again or enter manually.');
      setParsing(false);
    }
  };

  if (parsing) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div className="parsing">
          <div className="scan">
            {imageData ? (
              <img src={imageData} alt="Receipt" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : null}
          </div>
          <div>
            <div className="lbl">Computer vision · Parsing receipt</div>
            <div className="stat">Reading your receipt…</div>
            <div className="steps">
              {PARSE_STEPS.map((label) => (
                <div key={label} className="step active">
                  <span className="ind" />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="card">
        <div className="card-h">
          <h3>Scan a receipt</h3>
          <span className="sub">We'll auto-extract the details</span>
        </div>
        <div style={{ padding: 18 }}>
          {!selectedFile ? (
            <div
              className="upload"
              style={dragOver ? { borderColor: 'var(--primary)', background: 'var(--primary-soft)' } : undefined}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Icons.Camera size={28} stroke="var(--primary)" sw={1.4} />
              <div className="ttl">Snap or upload receipt</div>
              <div className="hint">Drop a photo here, or take one with your phone.</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn primary" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                  <Icons.Upload size={14} /> Upload photo
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) processFile(file);
                }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="rphoto" style={{ aspectRatio: 'auto', maxHeight: 320 }}>
                {imageData ? (
                  <img src={imageData} alt="Receipt" style={{ maxHeight: 320, width: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>Loading preview…</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => { setImageData(null); setSelectedFile(null); setError(''); }}
                >
                  Choose another
                </button>
                <button type="button" className="btn accent" style={{ flex: 1, justifyContent: 'center' }} onClick={handleParse}>
                  <Icons.Sparkle size={14} /> Scan receipt with AI
                </button>
              </div>
            </div>
          )}

          {error && (
            <p style={{ marginTop: 12, padding: '8px 11px', borderRadius: 8, background: 'oklch(0.96 0.03 25)', color: 'oklch(0.45 0.16 25)', fontSize: 12.5 }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
